import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Package, Truck, Home, CheckCircle2 } from "lucide-react";
import { publicTrackOrder, formatUsd } from "../lib/api";

const STAGES = [
  { key: "pending", label: "Order Received", icon: Package },
  { key: "paid", label: "Processing", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Home },
];

export default function OrderTrackPage() {
  const { ref } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    publicTrackOrder(ref).then(setData).catch(() => setErr("We couldn't find that order."));
  }, [ref]);

  if (err) return (
    <div className="max-w-2xl mx-auto text-center py-24 px-6">
      <div className="eyebrow">Not Found</div>
      <h1 className="font-serif text-4xl mt-4">We couldn't find that order.</h1>
      <p className="text-[#6B5757] mt-4">Double-check the link your clinic sent you.</p>
      <Link to="/" className="btn-primary inline-block mt-8">Back to ALTA</Link>
    </div>
  );
  if (!data) return <div className="min-h-[50vh] flex items-center justify-center font-serif text-2xl">Loading…</div>;

  const { order, clinic } = data;
  const brandColor = clinic?.brand_color || "#B85C56";
  const activeIdx = Math.max(0, STAGES.findIndex((s) => s.key === order.status));

  return (
    <div>
      {/* Co-branded header */}
      <section
        data-testid="track-header"
        className="text-white py-10"
        style={{ background: `linear-gradient(135deg, ${brandColor} 0%, #2A1F1F 100%)` }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16 flex flex-col md:flex-row justify-between gap-6 items-start md:items-center">
          <div>
            {clinic?.brand_logo_url ? (
              <img src={clinic.brand_logo_url} alt={clinic?.clinic_name} className="h-12 rounded" />
            ) : (
              <div className="font-serif text-2xl sm:text-3xl">{clinic?.clinic_name || "Your Clinic"}</div>
            )}
            <div className="text-sm mt-1 opacity-90">{clinic?.tagline}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[11px] tracking-widest uppercase opacity-80">Recovery Powered By</div>
            <div className="font-serif text-2xl mt-1">ALTA</div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16 py-16">
        <div className="max-w-2xl">
          <div className="font-mono text-xs tracking-widest text-[#B85C56]" data-testid="track-order-ref">
            Order {order.order_ref}
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">
            Hi {order.patient_name.split(" ")[0]}, your recovery system is on its way.
          </h1>
          <p className="text-[#6B5757] mt-6 leading-relaxed">
            {clinic?.contact_name || "Your surgeon"} has ordered an <em>{order.product_name}</em>{" "}
            to arrive before your surgery on <strong>{order.surgery_date}</strong>. Expected delivery:{" "}
            <strong>{order.delivery_date}</strong>.
          </p>
        </div>

        {/* Timeline */}
        <div className="mt-14">
          <div className="eyebrow">Status</div>
          <div className="mt-6 grid grid-cols-4 gap-2 relative">
            <div className="absolute top-6 left-6 right-6 h-px bg-[#E5CDBB]" aria-hidden />
            <div
              className="absolute top-6 left-6 h-px transition-all"
              style={{
                width: `calc(${(activeIdx / (STAGES.length - 1)) * 100}% - 24px)`,
                background: brandColor,
              }}
            />
            {STAGES.map((s, i) => {
              const done = i <= activeIdx;
              return (
                <div key={s.key} className="relative flex flex-col items-center text-center">
                  <div
                    className="w-12 h-12 rounded-full border-2 flex items-center justify-center bg-[#FBF6F1] z-10 transition-colors"
                    style={{
                      borderColor: done ? brandColor : "#E5CDBB",
                      background: done ? brandColor : "#FBF6F1",
                    }}
                  >
                    <s.icon className="w-5 h-5" style={{ color: done ? "#fff" : "#A08D8D" }} strokeWidth={1.5} />
                  </div>
                  <div className={`mt-3 text-xs font-mono uppercase tracking-widest ${done ? "text-[#2A1F1F]" : "text-[#A08D8D]"}`}>
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {order.surgeon_notes && (
          <div className="mt-16 soft-card p-8 max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: brandColor }}>
              A Note From Your Surgeon
            </div>
            <p className="mt-3 font-serif text-lg italic text-[#2A1F1F] leading-snug">"{order.surgeon_notes}"</p>
            <div className="mt-4 text-sm text-[#6B5757]">— {clinic?.contact_name}</div>
          </div>
        )}

        <div className="mt-14 soft-card p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#A08D8D]">System</div>
            <div className="font-serif text-lg mt-1">{order.product_name}</div>
            {order.swaps.length > 0 && (
              <div className="mt-2 text-[11px] font-mono tracking-wide uppercase text-[#B85C56]">
                ↳ Customized: {order.swaps.map(s => s.brand).join(" · ")}
              </div>
            )}
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#A08D8D]">Surgery Date</div>
            <div className="font-serif text-lg mt-1">{order.surgery_date}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#A08D8D]">Value</div>
            <div className="font-serif text-lg mt-1">{formatUsd(order.price)}</div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="eyebrow">Questions?</div>
          <p className="font-serif text-2xl mt-3">
            Contact {clinic?.clinic_name}{" "}
            <span className="text-[#6B5757]">or</span>{" "}
            <a href="mailto:support@altarecovery.com" style={{ color: brandColor }}>ALTA Support</a>
          </p>
        </div>
      </section>
    </div>
  );
}
